// Backend Service Layer - Updates existing services to use the mapper

import { backendMapper, BackendUser, BackendAuthResponse, BackendAuthUser, BackendEvent, BackendNotification, BackendRating, BackendPaymentPreference, BackendChatConfig } from '../../utils/mapper';

// Utility to transform RTK Query responses with mapper
export const transformResponse = <T, U>(mapperFn: (data: T, mapper: typeof backendMapper) => U) => 
  (response: any) => {
    const mappedResponse = backendMapper.mapApiResponse(response);
    if (mappedResponse.data) {
      return {
        ...mappedResponse,
        data: mapperFn(response.data!, backendMapper)
      };
    }
    return mappedResponse as any;
  };

// Utility to transform paginated responses
export const transformPaginatedResponse = <T, U>(mapperFn: (data: T[], mapper: typeof backendMapper) => U[]) => 
  (response: any) => {
    const mappedResponse = backendMapper.mapPaginatedResponse(response);
    if (mappedResponse.data?.items) {
      return {
        ...mappedResponse,
        data: {
          ...mappedResponse.data,
          items: mapperFn(mappedResponse.data.items as T[], backendMapper)
        }
      };
    }
    return mappedResponse;
  };

// Specific transformers for different entity types
export const transformUserResponse = transformResponse<BackendUser, any>((data, mapper) => mapper.mapUser(data));
export const transformUsersResponse = transformResponse<BackendUser[], any[]>((data, mapper) => mapper.mapUsers(data));
export const transformEventResponse = transformResponse<BackendEvent, any>((data, mapper) => mapper.mapEvent(data));
export const transformEventsResponse = transformResponse<BackendEvent[], any[]>((data, mapper) => mapper.mapEvents(data));
export const transformNotificationResponse = transformResponse<BackendNotification, any>((data, mapper) => mapper.mapNotification(data));
export const transformNotificationsResponse = transformResponse<BackendNotification[], any[]>((data, mapper) => mapper.mapNotifications(data));
export const transformRatingResponse = transformResponse<BackendRating, any>((data, mapper) => mapper.mapRating(data));
export const transformRatingsResponse = transformResponse<BackendRating[], any[]>((data, mapper) => mapper.mapRatings(data));
export const transformPaymentPreferenceResponse = transformResponse<BackendPaymentPreference, any>((data, mapper) => mapper.mapPaymentPreference(data));
export const transformChatConfigResponse = transformResponse<BackendChatConfig, any>((data, mapper) => mapper.mapChatConfig(data));

// Auth transformers
export const transformAuthResponse = transformResponse<BackendAuthResponse, any>((data, mapper) => mapper.mapAuthResponse(data));
export const transformAuthUserResponse = transformResponse<BackendAuthUser, any>((data, mapper) => mapper.mapAuthUser(data));

// Error handling wrapper for API calls
export const withErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  fallback?: T
): Promise<T> => {
  try {
    return await apiCall();
  } catch (error) {
    console.error('API call failed:', error);
    
    if (fallback !== undefined) {
      console.warn('Using fallback value due to API error');
      return fallback;
    }
    
    throw error;
  }
};

// Base transformer that can be used for simple responses
export const transformBasicResponse = (response: any): any => 
  backendMapper.mapApiResponse(response);

const backendTransformers = {
  transformResponse,
  transformPaginatedResponse,
  transformUserResponse,
  transformUsersResponse,
  transformEventResponse,
  transformEventsResponse,
  transformNotificationResponse,
  transformNotificationsResponse,
  transformRatingResponse,
  transformRatingsResponse,
  transformPaymentPreferenceResponse,
  transformChatConfigResponse,
  transformAuthResponse,
  transformAuthUserResponse,
  transformBasicResponse,
  withErrorHandling,
};

export default backendTransformers;